/* eslint-disable @typescript-eslint/no-explicit-any */
import { ServiceBus } from '@estee/elc-service-bus';
import {
    IServiceRegistry,
    IComponentViewController,
    IEcommApp,
    AllServicesTypes
} from './Service/ServiceUtils';

export const MOBILE_VERSION_SELECTOR = 'js-mobile-version';
export const DESKTOP_VERSION_SELECTOR = 'js-desktop-version';

export interface ISingleServiceInfo {
    [key: string]: string;
}

export interface IServiceInfo {
    [name: string]: ISingleServiceInfo;
}

export type ArrayMinOne<T> = {
    0: T;
} & T[];

interface IObjectOfStrings {
    [key: string]: string;
}

interface IServiceBusTopics {
    commands: IObjectOfStrings;
    events: IObjectOfStrings;
    queries: IObjectOfStrings;
}

export interface IPayload {
    url: string;
    body?: {};
    options?: {};
    headers?: {};
}

export interface IGetPayload {
    url: string;
    options?: {};
    headers?: {};
    params?: {};
}

export interface IRequestOptions {
    debounceInterval?: number;
}

export type HeaderType = 'Business-Unit' | 'Authorization' | 'AuthorizationToken' | 'ClientId';
export type HeaderObject = { [key in HeaderType]?: string };
export type IHeaderSetter = () => Promise<HeaderObject>;
export type IRequestTransformerConfig = { url: string } & RequestInit;
export type IRequestTransformer = (config: IRequestTransformerConfig) => Promise<void>;

export interface IHttpTransport {
    getStaticGlobalHeaders(): HeaderObject;
    setBaseUrl(baseUrl: string): void;
    addGlobalHeaders(headers: HeaderObject): void;
    removeGlobalHeader(headerName: string): void;
    addGlobalHeaderSetter(setter: IHeaderSetter): void;
    removeGlobalHeaderSetter(setter: IHeaderSetter): void;
    addRequestTransformer(transformer: IRequestTransformer): void;
    removeRequestTransformer(transformer: IRequestTransformer): void;
    delete(payload: string | IPayload): Promise<any>;
    post(payload: IPayload): Promise<any>;
    patch(payload: IPayload): Promise<any>;
    put(payload: IPayload): Promise<any>;
    get(payload: string | IGetPayload, IRequestOptions?: IRequestOptions): Promise<any>;
    readonly isAuthFailureHandlingEnabled: boolean;
    setAuthFailureHandlingEnabled(enabled: boolean): void;
}

export interface IUtagData {
    [key: string]: string | string[] | undefined | boolean | number | null | number[];
}
export interface IUtagCfgOvrd {
    noview: boolean;
    dom_complete?: boolean;
    lowerqp?: boolean;
    noload?: boolean | number;
    nocookie?: boolean;
    readywait?: boolean | number;
    waittimer?: number;
}
export interface IUtag {
    link(data: IUtagData, cb?: Function): void;
    view(data: IUtagData, cb?: Function): void;
}

export interface IFrontendLogger {
    isEnabled?: boolean;
    loggedServices?: {
        [key: string]: {
            isEnabled?: boolean;
        };
    };
}

interface IDrupalSite {
    translations?: any;
    trackingDataLayer?: any;
    client?: {
        subdomain?: string;
        isMobile?: number;
    };
    frontendLogger?: IFrontendLogger;
}
export type AnyObject = { [key: string]: any };
type ITranslationsCollection = { [key: string]: string | IObjectOfStrings };
type ViewDataPreloader = {
    addConfigs(configs: AnyObject): void;
    addTranslations(translations: ITranslationsCollection): void;
    getPreloadedConfigs(arg: string[]): AnyObject | null;
    getPreloadedTranslations(arg: string[]): ITranslationsCollection | null;
    preload(): Promise<void>;
    registerTranslationFieldsToPreload(fields?: string[]): void;
    registerConfigFieldsToPreload(fields?: string[]): void;
};
interface IComponentRenderer {
    init(): Promise<void>;
    getDataPreloader(): ViewDataPreloader | null;
    setDataPreloader(arg: ViewDataPreloader): void;
}

type IObjectLike = { [key: string]: any } | null;
export interface IViewManager {
    loadComponent(): Promise<void>;
    module: React.ComponentType<any> | null;
    controllerFromView: IComponentViewController | null;
}
export interface IViewsManager {
    lazyServicesStarted: boolean;
    getViewManager(viewName: string): IViewManager;
    startService(service: AllServicesTypes): Promise<void>;
    startLazyServices(): Promise<void>;
}

type ObjectWithNumbers = { [key: string]: number };

export interface ImPulse {
    sendMetric(name: string, value: number): void;
    sendMetrics(metrics: ObjectWithNumbers): void;
    sendTimer(name: string, value: number): void;
    sendTimers(metrics: ObjectWithNumbers): void;
}

declare global {
    // Augment Node.js `global`
    namespace NodeJS {
        interface Global {
            ServiceBusTopics: IServiceBusTopics;
            ServiceRegistry: IServiceRegistry;
            GlobalServiceBus: ServiceBus<any, any, any, any, any>;
            HttpTransport: IHttpTransport;
            __translationsCache__: { [key: string]: string };
            __configsCache__: { [key: string]: any };
            getPreloadedConfigs(fields?: string[]): IObjectLike;
            addConfigs(configs: object): void;
            getPreloadedTranslations(fields?: string[]): IObjectLike;
            addTranslations(translations: object): void;
            replaceSrc(originalSrc: string): string;
            utag_data: IUtagData;
            utag_cfg_ovrd: IUtagCfgOvrd;
            utag: IUtag;
            site?: IDrupalSite;
            location: Location;
            navigator: Navigator;
            ComponentRenderer: IComponentRenderer;
            ViewsManager: IViewsManager;
            registerTranslationFieldsToPreload: Function;
            registerConfigFieldsToPreload: Function;
            serviceInfo: IServiceInfo;
            getServiceVersions(): void;
            __ssr: boolean;
            __SENTRY__: IObjectLike;
            Sentry: IObjectLike;
            onerror(
                message: string,
                url: string,
                line: number,
                column: number,
                error: Error
            ): boolean;
            env: string;
            buid: string;
            requestIdleCallback: Function;
            cancelIdleCallback: Function;
        }
    }

    // Augment Browser `window`
    interface Window {
        EcommApp?: IEcommApp;
        ServiceBusTopics: IServiceBusTopics;
        __translationsCache__: { [key: string]: string };
        __configsCache__: { [key: string]: any };
        getPreloadedConfigs(fields?: string[]): IObjectLike;
        addConfigs(configs: object): void;
        getPreloadedTranslations(fields?: string[]): IObjectLike;
        addTranslations(translations: object): void;
        ServiceRegistry: IServiceRegistry;
        GlobalServiceBus: ServiceBus<any, any, any, any, any>;
        HttpTransport: IHttpTransport;
        replaceSrc(originalSrc: string): string;
        utag_data: any;
        utag_cfg_ovrd: IUtagCfgOvrd;
        utag: IUtag;
        location: Location;
        site?: IDrupalSite;
        addEventListener(a: string, b: Function): void;
        ComponentRenderer: IComponentRenderer;
        ViewsManager: IViewsManager;
        registerTranslationFieldsToPreload: Function;
        registerConfigFieldsToPreload: Function;
        getServiceVersions(): void;
        __ssr: boolean;
        onerror(message: string, url: string, line: number, column: number, error: Error): boolean;
        env: string;
        buid: string;
        serviceInfo: IServiceInfo;
        __SENTRY__: IObjectLike;
        Sentry: IObjectLike;
        requestIdleCallback: Function;
        cancelIdleCallback: Function;
        mPulse: ImPulse;
    }

    // Augment Web Worker `self`
    interface WorkerGlobalScope {
        ServiceBusTopics: IServiceBusTopics;
        ServiceRegistry: IServiceRegistry;
        GlobalServiceBus: ServiceBus<any, any, any, any, any>;
        HttpTransport: IHttpTransport;
        __translationsCache__: { [key: string]: string };
        __configsCache__: { [key: string]: any };
        getPreloadedConfigs(fields?: string[]): IObjectLike;
        addConfigs(configs: object): void;
        getPreloadedTranslations(fields?: string[]): IObjectLike;
        addTranslations(translations: object): void;
        replaceSrc(originalSrc: string): string;
        utag_data: IUtagData;
        utag_cfg_ovrd: IUtagCfgOvrd;
        __SENTRY__: IObjectLike;
        Sentry: IObjectLike;
        utag: IUtag;
        location: Location;
        addEventListener(a: string, b: Function): void;
        ComponentRenderer: IComponentRenderer;
        ViewsManager: IViewsManager;
        registerTranslationFieldsToPreload: Function;
        registerConfigFieldsToPreload: Function;
    }
}

export const isWebRendered = typeof self === 'object' && self.self === self;

export const root: NodeJS.Global | Window = typeof window === 'undefined' ? global : window;

export const setupServiceInfo = (serviceInfo: ISingleServiceInfo) => {
    const { name, ...rest } = serviceInfo;

    root.serviceInfo = root.serviceInfo || {};
    root.serviceInfo[name] = rest;
};

export enum ISandboxEnvironment {
    LIVE = 'live',
    FEATURE = 'feature',
    DEV = 'dev',
    STAGE = 'stage',
    CMS = 'cms',
    PERSONAL = 'personal',
    PRODUCTION = 'production'
}

export const getSandboxEnvironment: () => string = () => {
    let urlParts = root.location.hostname.split('.');
    const { LIVE, FEATURE, DEV, STAGE, CMS, PERSONAL } = ISandboxEnvironment;

    urlParts = urlParts.filter(part => !['www', 'm'].includes(part));
    const isLive = !(urlParts[0] === 'c') && !(urlParts[0] === 'e');

    return isLive
        ? LIVE
        : urlParts.includes(FEATURE)
        ? FEATURE
        : urlParts.includes(DEV)
        ? DEV
        : urlParts.includes(STAGE)
        ? STAGE
        : urlParts.includes(CMS)
        ? CMS
        : PERSONAL;
};
